// useEntitlements — the tenant's active product entitlements, for dynamic product
// visibility in the app (E4). Reads /tenants/me/features (the enabled entitlement codes).
//
// IMPORTANT: this is UX only — the gateway + services enforce (E3). And it FAILS OPEN:
// a tenant with NO entitlement data yet (not provisioned) sees everything its RBAC allows,
// so we never hide a product area from an un-migrated tenant. Only a tenant that HAS
// entitlements but not this product gets it hidden.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { getMyTenantFeatures } from '@/lib/hrApi';
import { currentIdentityKey, subscribeIdentity } from './entitlementIdentity';

// Catalogue product code → known legacy aliases (mirror of auth.products metadata.aliases
// for the product areas the app gates). Lets hasProduct() match whether the tenant was
// entitled with the catalogue code or a legacy feature code.
const ALIASES: Record<string, string[]> = {
  learning: ['training', 'training_module', 'lnd'],
  payroll: ['payroll_module', 'payrollProcessing', 'payslipGeneration'],
  credit: ['lending', 'loans'],
  benefits: ['benefits_management', 'benefits_module'],
  leave: ['leave_module', 'leaveCalendar', 'attendance'],
  performance: ['performance_reviews', 'performanceReviews'],
  developer_platform: ['apiV2', 'api_access'],
  // BA E3: the catalogue's aliases for brand_ambassadors (E0 seed metadata).
  brand_ambassadors: ['business_services.brand_ambassadors', 'ba'],
};

interface Entitlements {
  loaded: boolean;
  hasAny: boolean; // whether the tenant has ANY entitlement rows (drives fail-open)
  hasProduct: (code: string) => boolean;
  // hasProductStrict never fails open: returns true ONLY if the code (or an
  // alias) is explicitly enabled. Use for paid add-ons that must default OFF.
  hasProductStrict: (code: string) => boolean;
}

// ⚠️ The cache is per signed-in IDENTITY (tenant + user), not per page load
// (BA E3 follow-up, codex-reviewer): signing out, switching company or signing
// in as someone else happens without a full reload, and one module-wide set
// let the next account reuse the previous company's products — the BA gate
// included. Entries are keyed by identity, every identity change clears them,
// and a request that answers after the identity changed (or after an
// invalidation) is discarded instead of cached.
const cache = new Map<string, Set<string>>();
let generation = 0;
let lastIdentity: string | null = null;
let unwatch: (() => void) | null = null;
// The generation is OBSERVABLE (E3 follow-up 2, codex-reviewer-2): a mounted
// hook's local state is stamped with the generation it was loaded under and
// counts only while it is current. Matching on identity alone let
// A (entitled) → B (pending) → back to A show A's OLD answer.
const generationListeners = new Set<() => void>();
const subscribeGeneration = (cb: () => void) => {
  generationListeners.add(cb);
  return () => {
    generationListeners.delete(cb);
  };
};
const currentGeneration = () => generation;

// invalidateEntitlements clears the cache and makes every mounted hook refetch
// (call after toggling a feature so nav/gates update, and on sign-out).
// In-flight requests started before the call are discarded when they answer.
export function invalidateEntitlements() {
  cache.clear();
  generation += 1;
  generationListeners.forEach((l) => l());
}

// One store listener for the whole app: any identity transition (sign-out,
// tenant switch, a different user) drops every cached answer.
function watchIdentity() {
  if (unwatch) return;
  lastIdentity = currentIdentityKey();
  unwatch = subscribeIdentity(() => {
    const now = currentIdentityKey();
    if (now !== lastIdentity) {
      lastIdentity = now;
      invalidateEntitlements();
    }
  });
}

export function useEntitlements(): Entitlements {
  watchIdentity();
  const identity = useSyncExternalStore(subscribeIdentity, currentIdentityKey, currentIdentityKey);
  const gen = useSyncExternalStore(subscribeGeneration, currentGeneration, currentGeneration);
  const [state, setState] = useState<{ identity: string; gen: number; codes: Set<string> } | null>(() => {
    const hit = identity ? cache.get(identity) : undefined;
    return hit ? { identity, gen: generation, codes: hit } : null;
  });

  useEffect(() => {
    if (!identity) return; // signed out: nothing to fetch, nothing enabled
    const hit = cache.get(identity);
    if (hit) {
      setState({ identity, gen, codes: hit });
      return;
    }
    let alive = true;
    const settle = (set: Set<string>) => {
      // Discard an answer for a previous identity or a pre-invalidation request.
      if (gen !== generation || currentIdentityKey() !== identity) return;
      cache.set(identity, set);
      if (alive) setState({ identity, gen, codes: set });
    };
    getMyTenantFeatures()
      .then((rows) => settle(new Set((rows ?? []).filter((r) => r.isEnabled).map((r) => r.featureCode))))
      // On failure, fail open (empty set → hasAny false → everything shows);
      // hasProductStrict stays closed.
      .catch(() => settle(new Set()));
    return () => {
      alive = false;
    };
  }, [identity, gen]);

  // Only an answer for the CURRENT identity AND generation counts; anything
  // else (another account, or this account before a switch-away or an
  // invalidation) is "not loaded" until the refetch answers.
  const current = state && state.identity === identity && state.gen === gen ? state.codes : null;
  const loaded = current !== null;
  const set = current ?? new Set<string>();
  const hasAny = set.size > 0;

  return {
    loaded,
    hasAny,
    hasProduct: (code: string) => {
      if (!hasAny) return true; // fail open for un-provisioned tenants
      if (set.has(code)) return true;
      return (ALIASES[code] ?? []).some((a) => set.has(a));
    },
    hasProductStrict: (code: string) => {
      if (set.has(code)) return true;
      return (ALIASES[code] ?? []).some((a) => set.has(a));
    },
  };
}
